import type { Collector } from './types';
import { prop, safe, signal, unavailable, withTimeout } from './util';

interface BatteryLike {
  level: number;
  charging: boolean;
}
interface ConnectionLike {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

export const hardwareCollector: Collector = {
  id: 'hardware',
  title: 'Hardware',
  description: 'CPU cores, memory, touch support and other device characteristics exposed to web pages.',
  async collect() {
    if (typeof navigator === 'undefined') return [unavailable('hw.cores', 'CPU cores', 'navigator is not available')];
    const nav = navigator;

    return Promise.all([
      safe('hw.cores', 'CPU cores (logical)', () => {
        const n = nav.hardwareConcurrency;
        return typeof n === 'number'
          ? signal('hw.cores', 'CPU cores (logical)', n)
          : unavailable('hw.cores', 'CPU cores (logical)', 'Not exposed');
      }),

      safe('hw.memory', 'Device memory', () => {
        const gb = prop<number>(nav, 'deviceMemory');
        return typeof gb === 'number'
          ? signal('hw.memory', 'Device memory', gb, { display: `${gb} GB (rounded, capped by the browser)` })
          : unavailable('hw.memory', 'Device memory', 'Not supported (Chromium-only API)');
      }),

      safe('hw.touch', 'Touch points', () => {
        const n = nav.maxTouchPoints;
        return typeof n === 'number'
          ? signal('hw.touch', 'Touch points', n, { display: n === 0 ? '0 (no touch screen)' : String(n) })
          : unavailable('hw.touch', 'Touch points', 'Not exposed');
      }),

      safe('hw.battery', 'Battery', async () => {
        const getBattery = prop<() => Promise<BatteryLike>>(nav, 'getBattery');
        if (typeof getBattery !== 'function')
          return unavailable('hw.battery', 'Battery', 'Not supported (deprecated, Chromium-only API)');
        const b = await withTimeout(getBattery.call(nav), 1000, 'getBattery');
        const value = { level: Math.round(b.level * 100) / 100, charging: b.charging };
        return signal('hw.battery', 'Battery', value, {
          display: `${Math.round(b.level * 100)}% · ${b.charging ? 'charging' : 'not charging'}`,
          note: 'Deprecated Chromium API. Changes constantly, so it only helps link visits within minutes.',
        });
      }),

      safe('hw.connection', 'Network connection hints', () => {
        const c = prop<ConnectionLike>(nav, 'connection');
        if (!c) return unavailable('hw.connection', 'Network connection hints', 'Not supported (Chromium-only API)');
        const value = {
          effectiveType: c.effectiveType ?? null,
          downlink: c.downlink ?? null,
          rtt: c.rtt ?? null,
          saveData: c.saveData ?? null,
        };
        return signal('hw.connection', 'Network connection hints', value, {
          display: `type: ${value.effectiveType ?? '?'} · downlink: ${value.downlink ?? '?'} Mb/s · rtt: ${value.rtt ?? '?'} ms${value.saveData ? ' · data saver on' : ''}`,
          note: 'Rounded by the browser and fluctuates; not part of the stable hash.',
        });
      }),

      safe('hw.gamepads', 'Connected gamepads', () => {
        if (typeof nav.getGamepads !== 'function') return unavailable('hw.gamepads', 'Connected gamepads', 'Gamepad API not supported');
        // Reading the list does not prompt. Browsers hide pads until a button is pressed on this page.
        const pads = Array.from(nav.getGamepads()).filter((p): p is Gamepad => !!p);
        return signal('hw.gamepads', 'Connected gamepads', pads.length, {
          display: pads.length ? `${pads.length}: ${pads.map((p) => p.id).join(', ')}` : '0 visible',
          note: 'Browsers only reveal gamepads after you press a button on one while the page is open.',
        });
      }),
    ]);
  },
};
